/* eslint-disable react/display-name */
import {
  find,
  flattenDeep,
  get,
  isArray,
  isEmpty,
  isFunction,
  noop,
} from 'lodash'
import React, { useMemo } from 'react'
import ReactSelect, { createFilter } from 'react-select'

import {
  flattenGroups,
  getCustomElement,
  getCustomStyle,
  getOptions,
  IndicatorsContainer,
  IndicatorSeparator,
} from '../Select/helpers'
import { TExtraProps, TOverwriteProps, TSelect } from '../Select/types'

const SelectReact = (props: TSelect): React.ReactElement => {
  const {
    direct,
    simpleValue,
    valuePrefix,
    isMulti = false,
    multi = false,
    slim,
    small,
    useBody,
    permanentPlaceholder,
    noStyle,
    isInvalid,
    customPlaceholder,
    placeholder,
    menuPlacement = 'auto',
    noOptionsMessage = i18n.t('no_option', 'Keine Optionen'),
    hideDropdown,
    enableiOSResizeFix,
    customDropdownIndicator,
    color = 'white',
    artificalDarkMode = false,
    clearable,
    isClearable,
    disabled,
    isDisabled,
    hasTooltip = false,
    isSearchable = true,
    isFilterable = false,
    searchable = true,
    styles = {},
    ...rest
  } = props

  const options = useMemo(() => {
    return getOptions(props.options ? props.options : [])
  }, [props.options])

  const hideDropdowComponents =
    hideDropdown &&
    React.isValidElement(IndicatorSeparator) &&
    React.isValidElement(IndicatorsContainer)
      ? { IndicatorSeparator, IndicatorsContainer }
      : {}

  const customComponents = useMemo(() => {
    return getCustomElement({
      hideDropdowComponents,
      color,
      customDropdownIndicator,
      valuePrefix,
      slim,
      hasTooltip,
      propComponents: props.components,
      isMulti: isMulti || multi,
    })
  }, [
    hideDropdowComponents,
    color,
    customDropdownIndicator,
    valuePrefix,
    slim,
    hasTooltip,
    props.components,
  ])

  const customStyles = useMemo(() => {
    return getCustomStyle({
      styles,
      enableiOSResizeFix,
      small,
      noStyle,
      artificalDarkMode,
      color,
      isInvalid,
    })
  }, [
    styles,
    enableiOSResizeFix,
    small,
    noStyle,
    artificalDarkMode,
    color,
    isInvalid,
  ])

  let value = props.value
  if ((direct || simpleValue) && !permanentPlaceholder) {
    const flattenedOptions = flattenDeep(flattenGroups(options))
    if (isMulti || multi) {
      value =
        !value || value === '' || !isArray(value) || isEmpty(value) ? [] : value
      value = (value || []).map((v) =>
        find(flattenedOptions, (o) => o.value === v),
      )
    } else {
      value = find(flattenedOptions, (o) => o.value === value)
    }
  }

  const extraProps: TExtraProps = {
    isMulti: isMulti || multi,
  }

  if (useBody) {
    extraProps.menuPortalTarget = document.querySelector('body')
  }

  const overwriteProps: TOverwriteProps = {}

  if (permanentPlaceholder) {
    overwriteProps.value = { label: permanentPlaceholder, value: null }
  }

  const onChange = (value): void => {
    if (!props.onChange) {
      return
    }
    if (direct || simpleValue) {
      if (isMulti || multi) {
        const result = (!value || value === '' ? [] : value).map((v) =>
          get(v, 'value'),
        )
        props.onChange(result || [])
      } else {
        props.onChange(get(value, 'value'))
      }
    } else {
      props.onChange(value)
    }
  }

  return (
    <ReactSelect
      {...rest}
      {...extraProps}
      options={options}
      onChange={onChange}
      autoComplete="new-password"
      value={value === undefined ? null : value}
      onBlur={(event) => event.preventDefault()}
      components={customComponents}
      filterOption={createFilter({ ignoreAccents: false })}
      ignoreAccents={false}
      isDisabled={disabled || isDisabled}
      styles={customStyles}
      placeholder={
        placeholder ||
        customPlaceholder ||
        `${i18n.t('select_placeholder', 'Wählen...')}`
      }
      noOptionsMessage={
        isFunction(noOptionsMessage)
          ? noOptionsMessage
          : () => noOptionsMessage ?? ''
      }
      menuPlacement={menuPlacement}
      onCreateOption={noop}
      className="react-select"
      isClearable={isClearable || clearable}
      isMulti={multi || isMulti}
      isSearchable={searchable || isSearchable}
      isFilterable={isFilterable}
      {...overwriteProps}
    />
  )
}

export default SelectReact
